import { h } from 'vue'
import { createRouter, createWebHistory, RouterView } from 'vue-router'
import CityExport from '@/views/CityExport.vue'
import ExploreFeed from '@/views/ExploreFeed.vue'
import SubmissionForm from '@/views/SubmissionForm.vue'
import UserDashboard from '@/views/UserDashboard.vue'
import ViewSubmission from '@/views/ViewSubmission.vue'
import ProjectInfo from '@/views/ProjectInfo.vue'
import SettingsScreen from '@/views/SettingsScreen.vue'
import LandingPage from '@/views/LandingPage.vue'
import AccessibilityStatement from '@/views/AccessibilityStatement.vue'
import LegalPage from '@/views/LegalPage.vue'
const CitiesPage = () => import('@/views/CitiesPage.vue')
const CityLayout = () => import('@/views/CityLayout.vue')
const CityOverview = () => import('@/views/CityOverview.vue')
const CityReports = () => import('@/views/CityReports.vue')
const CityMapPage = () => import('@/views/CityMapPage.vue')
const CitySettings = () => import('@/views/CitySettings.vue')
const CityKit = () => import('@/views/CityKit.vue')
import { preferredLocale, applyLocale, SUPPORTED } from '@/i18n'

// Every page lives under /en/... or /de/... so each language has its own indexable URL.
// seo: key into locales/<lang>/meta.json; index: false => <meta robots noindex>
const children = [
  { path: '', name: 'LandingPage', component: LandingPage, meta: { seo: 'landing', index: true, layout: 'full' } },
  { path: 'explore', name: 'Explore', component: ExploreFeed, meta: { seo: 'explore', index: true } },
  { path: 'submit', name: 'Submit', component: SubmissionForm, meta: { seo: 'submit', index: false } },
  { path: 'profile', name: 'Profile', component: UserDashboard, meta: { seo: 'profile', index: false } },
  { path: 'settings', name: 'Settings', component: SettingsScreen, meta: { seo: 'settings', index: false } },
  { path: 'about', name: 'About', component: ProjectInfo, meta: { seo: 'about', index: true, layout: 'wide' } },
  { path: 'accessibility', name: 'Accessibility', component: AccessibilityStatement, meta: { seo: 'accessibility', index: true, layout: 'wide' } },
  { path: 'impressum', name: 'Imprint', component: LegalPage, props: { kind: 'imprint' }, meta: { seo: 'imprint', index: true, layout: 'wide' } },
  { path: 'datenschutz', name: 'Privacy', component: LegalPage, props: { kind: 'privacy' }, meta: { seo: 'privacy', index: true, layout: 'wide' } },
  { path: 'kommunen', name: 'Cities', component: CitiesPage, meta: { seo: 'cities', index: true, layout: 'wide' } },
  {
    path: 'city',
    component: CityLayout,
    meta: { layout: 'full' },
    children: [
      { path: '', name: 'City', component: CityOverview, meta: { seo: 'city', index: false } },
      { path: 'reports', name: 'CityReports', component: CityReports, meta: { seo: 'city', index: false } },
      { path: 'map', name: 'CityMap', component: CityMapPage, meta: { seo: 'city', index: false } },
      { path: 'kit', name: 'CityKit', component: CityKit, meta: { seo: 'cityKit', index: false } },
      { path: 'export', name: 'CityExport', component: CityExport, meta: { seo: 'city', index: false } },
      { path: 'settings', name: 'CitySettings', component: CitySettings, meta: { seo: 'city', index: false } },
    ],
  },
  { path: 'view/:id', name: 'ViewSubmission', component: ViewSubmission, meta: { seo: 'view', index: true } }, // public report text is indexable (server/index.js renders it); photos are not used as preview
]

const langPattern = SUPPORTED.join('|')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: () => `/${preferredLocale()}/` },
    {
      path: '/survey',
      name: 'Survey',
      beforeEnter() {
        // using location.href to force a full page reload
        location.href = 'https://quant-ux.com/#/test.html?h=a2aa10aK4b30fYuGMoc1yGdL4OyRekszMqI26zAhVhGfMFIMZMV2w9KNJ7y2&ln=en'
      },
      component: LandingPage,
    },
    { path: `/:lang(${langPattern})`, component: { render: () => h(RouterView) }, children },
    // legacy, unprefixed URLs (/explore, /view/12) -> /<lang>/explore
    {
      path: '/:pathMatch(.*)*',
      redirect: (to) => {
        const lang = preferredLocale()
        const rest = to.path.replace(/\/+$/, '')
        if (new RegExp(`^/(${langPattern})(/|$)`).test(rest)) return { path: `/${lang}/`, replace: true }
        return { path: `/${lang}${rest}`, query: to.query, hash: to.hash }
      },
    },
  ],
})

// The URL decides the language
router.beforeEach((to) => {
  if (to.params.lang) applyLocale(to.params.lang)
})

export default router
